"use client";

import clsx from "clsx";
import {useRef, useState, useCallback, useEffect, type Dispatch, type JSX, type SetStateAction} from "react";
import {useTranslation} from "react-i18next";
import {NextStepViewport} from "nextstepjs";
import Icon, {ICON_SIZES} from "../library/Icon";
import {SIZES} from "@/enums/library";
import {useBanner} from "@/hooks/useBanner";
import useMediaQuery from "@/hooks/useMediaQuery";
import {FLOATING_CLEARANCE_PROPS} from "@/utils/common/floatingClearance";
import Button from "../library/Button";
import LuxContainer from "../library/Container";

type FocusPanelButton = {
   label?: React.ReactNode;
   onClick: () => void;
   disabled?: boolean;
   /** Shown when the action is restricted (e.g. plan limits); passed to Button. */
   restrictedText?: string;
};

type ContainerProps = {
   children: string | JSX.Element | JSX.Element[];
   title?: string | JSX.Element | JSX.Element[];
   subTitle?: string | JSX.Element | JSX.Element[];
   size?: Extract<SIZES, SIZES.SM | SIZES.BASE | SIZES.LG | SIZES.XL | SIZES.FULL>;
   helpLinkUrl?: string;
   maxSubTitleWidthClass?: string;
   isLoading?: boolean;
   hideHeader?: boolean;
   headerButton?: {
      label: string | JSX.Element | JSX.Element[];
      icon?: React.ReactNode;
      disabled?: boolean;
      id?: string;
      className?: string;
      type?: "primary" | "secondary" | "tertiary" | "none" | "link";
      compact?: boolean;
      target?: "_blank" | "_self" | "_parent" | "_top";
   } & (
      | {
           href: string;
        }
      | {
           onClick: () => void;
        }
   );
   headerContent?: React.ReactNode;
   sidePanel?: {
      title?: string | JSX.Element;
      topAction?: React.ReactNode;
      content: React.ReactNode;
      bottom?: React.ReactNode;
      width?: number; // in pixels, defaults to 300
      breakpoint?: number; // in pixels, defaults to 1200
   };
   focusPanel?: {
      /** Let a focused review keep its own decision bar sticky instead of the page navigation. */
      stickyHeader?: boolean;
      backButton?: FocusPanelButton;
      secondaryButton?: FocusPanelButton;
      nextButton?: FocusPanelButton;
      /** Full width above footer buttons (e.g. validation messages). */
      footerAlert?: React.ReactNode;
      /**
       * When false, footer actions (Back / Save / Next) do not scroll the main column to the top before running their handler.
       * Default true.
       */
      scrollMainToTopOnFooterAction?: boolean;
      /** Rendered inside the sticky header, below the title/subtitle but above the fade gradient. */
      subHeaderContent?: React.ReactNode;
   };
   ignorePadding?: boolean;
   /** When set, wraps the scrollable content with NextStepViewport for guided tour targeting */
   tourViewportId?: string;
   /**
    * When true (with focusPanel), the sticky header hides while scrolling down and reappears when scrolling up.
    * Near the top of the scroll area it stays visible.
    */
   autoHideHeader?: boolean;
};

const AUTO_HIDE_SCROLL_TOP_EPSILON = 10;
const AUTO_HIDE_SCROLL_DELTA_THRESHOLD = 5;

function applyAutoHideHeaderFromScroll(
   scrollTop: number,
   previousScrollTop: number,
   setHeaderVisible: Dispatch<SetStateAction<boolean>>
) {
   if (scrollTop < AUTO_HIDE_SCROLL_TOP_EPSILON) {
      setHeaderVisible(true);
      return;
   }
   const diff = scrollTop - previousScrollTop;
   if (Math.abs(diff) <= AUTO_HIDE_SCROLL_DELTA_THRESHOLD) {
      return;
   }
   setHeaderVisible(diff < 0);
}

export default function Container({
   children,
   title,
   subTitle,
   size = SIZES.LG,
   helpLinkUrl,
   maxSubTitleWidthClass,
   isLoading,
   hideHeader,
   headerButton,
   headerContent,
   sidePanel,
   focusPanel,
   ignorePadding = false,
   tourViewportId,
   autoHideHeader,
}: Readonly<ContainerProps>) {
   const {t: ct} = useTranslation("common");
   const {isBannerActive} = useBanner();
   const sidePanelWidth = sidePanel?.width ?? 300;
   const sidePanelBreakpoint = sidePanel?.breakpoint ?? 1200;
   const isLargeScreen = useMediaQuery(`(min-width: ${sidePanelBreakpoint}px)`);
   const scrollContainerRef = useRef<HTMLDivElement>(null);
   const lastScrollTopRef = useRef(0);
   const [canScrollDown, setCanScrollDown] = useState(false);
   const [headerVisible, setHeaderVisible] = useState(true);

   const checkScroll = useCallback(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      const threshold = 8;
      setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > threshold);

      if (!autoHideHeader) {
         lastScrollTopRef.current = el.scrollTop;
         return;
      }

      const scrollTop = el.scrollTop;
      applyAutoHideHeaderFromScroll(scrollTop, lastScrollTopRef.current, setHeaderVisible);
      lastScrollTopRef.current = scrollTop;
   }, [autoHideHeader]);

   useEffect(() => {
      if (!autoHideHeader) {
         setHeaderVisible(true);
      }
   }, [autoHideHeader]);

   useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      const frame = requestAnimationFrame(checkScroll);
      const observer = new ResizeObserver(checkScroll);
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
      return () => {
         cancelAnimationFrame(frame);
         observer.disconnect();
      };
   }, [checkScroll]);

   const handleFocusPanelButtonClick = (onClick: () => void) => {
      if (focusPanel?.scrollMainToTopOnFooterAction !== false) {
         scrollContainerRef.current?.scrollTo({top: 0, behavior: "smooth"});
      }
      onClick();
   };

   const headerSection = (
      <div
         className={clsx({
            "flex flex-wrap items-center justify-between":
               headerButton || helpLinkUrl || headerContent,
         })}
      >
         {isLoading ? (
            <div className={clsx("mb-3 rounded-full w-1/3 h-8 animate-pulse")}>
               <div className={clsx("h-8", "w-1/3", "bg-400/50", "rounded-full")} />
            </div>
         ) : (
            title && <h1 className={clsx("title")}>{title}</h1>
         )}
         {(headerButton || helpLinkUrl || headerContent) && (
            <div className={clsx("flex", "items-center", "gap-2")}>
               {headerButton && (
                  <Button
                     id={headerButton?.id}
                     type={headerButton?.type ?? "primary"}
                     className={clsx("my-2 md:my-0 !mb-2", headerButton?.className)}
                     onClick={
                        "onClick" in headerButton && typeof headerButton.onClick === "function"
                           ? headerButton.onClick
                           : undefined
                     }
                     href={
                        "href" in headerButton && typeof headerButton.href === "string"
                           ? headerButton.href
                           : undefined
                     }
                     disabled={headerButton?.disabled}
                     compact={headerButton?.compact}
                     target={headerButton?.target}
                  >
                     {headerButton?.icon && <span className="mr-2">{headerButton.icon}</span>}
                     {headerButton?.label}
                  </Button>
               )}
               {helpLinkUrl && (
                  <Button
                     compact
                     type="secondary"
                     className={clsx("whitespace-nowrap")}
                     target="_blank"
                     href={helpLinkUrl}
                  >
                     {ct("needHelp")}
                  </Button>
               )}
               {headerContent}
            </div>
         )}
      </div>
   );

   const subTitleSection = isLoading ? (
      <div className={clsx("mb-4", maxSubTitleWidthClass)}>
         <div className={clsx("animate-pulse")}>
            <div className={clsx("h-5", "w-1/2", "bg-400/50", "rounded-full")} />
         </div>
      </div>
   ) : (
      subTitle && (
         <div className={clsx("mb-4", maxSubTitleWidthClass)}>
            <span className="subTitle">{subTitle}</span>
         </div>
      )
   );

   return (
      <div
         className={clsx(
            "flex",
            "lg:flex-row",
            "flex-col",
            "overflow-hidden",
            // Mobile: subtract 64px for mobile header; Desktop (lg): full height or minus banner
            isBannerActive
               ? "min-h-[calc(100vh-134px)] max-h-[calc(100vh-134px)] md:min-h-[calc(100vh-70px)] md:max-h-[calc(100vh-70px)]"
               : "min-h-[calc(100vh-64px)] max-h-[calc(100vh-64px)] md:min-h-screen md:max-h-screen"
         )}
      >
         <div
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className={clsx("flex-1", "min-w-0", "overflow-y-auto", focusPanel && "flex flex-col")}
         >
            {focusPanel ? (
               <div className={clsx("flex", "flex-col", "grow")}>
                  <LuxContainer
                     size={SIZES.LG}
                     className={clsx("p-4", "lg:!p-12", "flex-1", "flex", "flex-col", "w-full")}
                  >
                     {!hideHeader && (
                        <div
                           className={clsx(
                              focusPanel.stickyHeader !== false && "sticky",
                              "top-0",
                              "z-10",
                              "relative",
                              "!bg-[#f9f9f9]",
                              "dark:!bg-[#252930]",
                              "-mx-4",
                              "lg:!-mx-12",
                              "px-4",
                              "lg:!px-12",
                              "pt-4",
                              "lg:!pt-12",
                              "-mt-4",
                              "lg:!-mt-12",
                              "pb-4",
                              "after:content-['']",
                              "after:absolute",
                              "after:left-0",
                              "after:right-0",
                              "after:bottom-0",
                              "after:translate-y-full",
                              "after:h-6",
                              "after:bg-gradient-to-b",
                              "after:from-[#f9f9f9]",
                              "after:to-transparent",
                              "dark:after:from-[#252930]",
                              "after:pointer-events-none",
                              autoHideHeader && [
                                 "transition-transform",
                                 "duration-300",
                                 headerVisible ? "translate-y-0" : "-translate-y-full",
                              ]
                           )}
                        >
                           {headerSection}
                           {subTitleSection}
                           {focusPanel.subHeaderContent && (
                              <div className="[&>*:last-child]:mb-0">
                                 {focusPanel.subHeaderContent}
                              </div>
                           )}
                        </div>
                     )}

                     {/* Content */}
                     <div className={clsx("flex-1", "w-full", !hideHeader && "pt-6")}>{children}</div>
                  </LuxContainer>

                  {/* Sticky bottom panel - full width outside LuxContainer */}
                  <div
                     {...FLOATING_CLEARANCE_PROPS}
                     className={clsx(
                        "sticky",
                        "bottom-0",
                        "z-10",
                        "bg-surface",
                        "py-4",
                        "border-t",
                        "border-neutral",
                        "relative"
                     )}
                  >
                     {canScrollDown && (
                        <div
                           className="absolute left-0 right-0 bottom-full h-8 bg-gradient-to-t from-[#f9f9f9] dark:from-[#252930] to-transparent pointer-events-none flex items-end justify-center pb-1"
                           aria-hidden
                        >
                           <Icon
                              icon="chevron-down"
                              size={ICON_SIZES.SM}
                              className="text-muted-foreground animate-bounce"
                           />
                        </div>
                     )}
                     <LuxContainer size={SIZES.LG} className={clsx("px-4", "lg:!px-12")}>
                        <div className={clsx("flex", "flex-col", "gap-3", "w-full")}>
                           {focusPanel.footerAlert && (
                              <div className={clsx("w-full", "min-w-0")}>{focusPanel.footerAlert}</div>
                           )}
                           <div className={clsx("flex", "items-center", "justify-between")}>
                           <div>
                              {focusPanel.backButton && (
                                 <Button
                                    type="tertiary"
                                    onClick={() =>
                                       handleFocusPanelButtonClick(focusPanel.backButton!.onClick)
                                    }
                                    disabled={focusPanel.backButton.disabled}
                                    restrictedText={focusPanel.backButton.restrictedText}
                                 >
                                    {focusPanel.backButton.label ?? ct("back")}
                                 </Button>
                              )}
                           </div>
                           <div
                              className={clsx(
                                 "flex",
                                 "items-center",
                                 "gap-3",
                                 "mr-16",
                                 "lg:mr-0"
                              )}
                           >
                              {focusPanel.secondaryButton && (
                                 <Button
                                    type="secondary"
                                    onClick={() =>
                                       handleFocusPanelButtonClick(
                                          focusPanel.secondaryButton!.onClick
                                       )
                                    }
                                    disabled={focusPanel.secondaryButton.disabled}
                                    restrictedText={focusPanel.secondaryButton.restrictedText}
                                 >
                                    {focusPanel.secondaryButton.label ?? ct("save")}
                                 </Button>
                              )}
                              {focusPanel.nextButton && (
                                 <Button
                                    type="primary"
                                    onClick={() =>
                                       handleFocusPanelButtonClick(focusPanel.nextButton!.onClick)
                                    }
                                    disabled={focusPanel.nextButton.disabled}
                                    restrictedText={focusPanel.nextButton.restrictedText}
                                 >
                                    {focusPanel.nextButton.label ?? ct("next")}
                                 </Button>
                              )}
                           </div>
                        </div>
                        </div>
                     </LuxContainer>
                  </div>
               </div>
            ) : (
               <>
                  <MaybeViewport id={tourViewportId}>
                     <LuxContainer
                        size={ignorePadding ? SIZES.FULL : size}
                        className={clsx({
                           "p-4": !ignorePadding,
                           "lg:!p-12": !ignorePadding,
                        })}
                     >
                        {headerSection}
                        {subTitleSection}
                        {children}
                     </LuxContainer>
                  </MaybeViewport>
                  {canScrollDown && (
                     <div
                        className="pointer-events-none sticky bottom-0 h-8 -mt-8 bg-gradient-to-t from-[#f9f9f9] dark:from-[#252930] to-transparent flex items-end justify-center pb-1"
                        aria-hidden
                     >
                        <Icon
                           icon="chevron-down"
                           size={ICON_SIZES.SM}
                           className="text-muted-foreground animate-bounce"
                        />
                     </div>
                  )}
               </>
            )}
         </div>

         {/* Side panel - only visible on lg+ screens */}
         {sidePanel && (
            <div
               className={clsx("hidden", "bg-panel", "flex", "flex-col")}
               style={{
                  display: isLargeScreen ? "flex" : "none",
                  width: `${sidePanelWidth}px`,
               }}
            >
               {/* Header section */}
               {(sidePanel.title || sidePanel.topAction) && (
                  <div className={clsx("py-4", "px-4", "border-b", "border-neutral")}>
                     <div className={clsx("flex", "items-center", "justify-between")}>
                        {sidePanel.title && (
                           <h2 className={clsx("text-lg", "font-semibold", "text-800")}>
                              {sidePanel.title}
                           </h2>
                        )}
                        {sidePanel.topAction && (
                           <div className={clsx("flex-shrink-0")}>{sidePanel.topAction}</div>
                        )}
                     </div>
                  </div>
               )}

               {/* Scrollable content section */}
               <div className={clsx("flex-1", "overflow-y-auto", "py-2", "px-4")}>
                  {sidePanel.content}
               </div>

               {/* Bottom section */}
               {sidePanel.bottom && (
                  <div className={clsx("py-4", "px-4", "border-t", "border-neutral")}>
                     {sidePanel.bottom}
                  </div>
               )}
            </div>
         )}
      </div>
   );
}

function MaybeViewport({id, children}: Readonly<{id?: string; children: React.ReactNode}>) {
   if (!id) return <>{children}</>;
   return <NextStepViewport id={id}>{children}</NextStepViewport>;
}
